import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { servicesQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { getMasqBarVisibility, announceMasqReadOnlyChange } from '@/utils/masq';

const ACCOUNT_LOOKUP_DEBOUNCE_MS = 300;

/**
 * Hook providing masquerade state and actions.
 */
export function useMasq() {
  const auth = useAuth();
  const location = useLocation();
  const [readOnly, setReadOnlyState] = useState(true);

  const setReadOnly = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setReadOnlyState((prev) => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      announceMasqReadOnlyChange(newValue);
      return newValue;
    });
  }, []);

  const toggleReadOnly = useCallback(() => {
    setReadOnly((prev) => !prev);
  }, [setReadOnly]);

  const isMasqOn = auth.account.is_masq === true;
  const barVisible = getMasqBarVisibility();
  const isAllowed = barVisible && ((auth.account.admin === true && auth.user.can_masquerade === true) || isMasqOn);

  const masqueradeInto = useCallback(
    (accountId: string) => {
      if (isEmptyString(accountId)) return;

      const isWorkspaceRoute = location.pathname.includes('/workspaces/');

      if (isWorkspaceRoute) {
        window.location.href = `/?masq_account_id=${accountId.trim()}`;
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('masq_account_id', accountId.trim());
        window.location.href = url.toString();
      }
    },
    [location.pathname],
  );

  const unmasquerade = useCallback(() => {
    const isWorkspaceRoute = location.pathname.includes('/workspaces/');

    if (isWorkspaceRoute) {
      window.location.href = '/';
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('masq_account_id');
      window.location.href = url.toString();
    }
  }, [location.pathname]);

  return {
    isMasqOn,
    isAllowed,
    readOnly,
    setReadOnly,
    toggleReadOnly,
    masqueradeInto,
    unmasquerade,
  };
}

/**
 * Hook to validate and fetch account info for the masq form.
 * Debounces the input to avoid firing a request on every keystroke.
 */
export function useAccountLookup(accountId: string) {
  const trimmedId = accountId.trim();
  const numericId = Number(trimmedId);
  const isValid = !isEmptyString(trimmedId) && Number.isInteger(numericId) && numericId > 0;

  const [debouncedId] = useDebouncedValue(trimmedId, ACCOUNT_LOOKUP_DEBOUNCE_MS);

  const debouncedNumericId = Number(debouncedId);
  const isDebouncedValid =
    !isEmptyString(debouncedId) && Number.isInteger(debouncedNumericId) && debouncedNumericId > 0;

  const query = servicesQuery.getAccountById.useQuery({
    queryKey: ['masq-account-lookup', debouncedId],
    queryData: {
      params: { accountId: debouncedNumericId },
    },
    enabled: isDebouncedValid,
    retry: false,
    staleTime: 30_000,
    select: (data) => ({
      id: data.body.account.id,
      name: data.body.account.name,
      dataCenter: data.body.account.data_center,
    }),
  });

  return {
    isValid,
    isLoading: (query.isLoading && isDebouncedValid) || (isValid && trimmedId !== debouncedId),
    accountInfo: query.data ?? null,
    error: query.error,
  };
}

/**
 * Hook to fetch workspace details (shard, identifiers) from `/v1/environments/:id`.
 * The auth payload doesn't include shard, so we need a separate fetch.
 */
export function useWorkspaceDetails(workspaceId: number | undefined) {
  return servicesQuery.getEnvironment.useQuery({
    queryKey: ['masq-workspace-details', String(workspaceId ?? '')],
    queryData: {
      params: { environmentId: workspaceId ?? 0 },
    },
    enabled: workspaceId != null,
    retry: false,
    staleTime: 60_000,
    select: (data) => ({
      shard: data.body.environment.shard,
      identifier_type: data.body.environment.identifier_type,
      identifiers: data.body.environment.identifiers,
    }),
  });
}

/**
 * Hook to resolve a user ID to an email using the account's user list.
 * Returns the email if found, otherwise the raw ID string.
 */
export function useAccountUsers(accountId: number | undefined) {
  const query = servicesQuery.getAllUsers.useQuery({
    queryKey: ['masq-account-users', String(accountId ?? '')],
    queryData: {
      params: { account_id: accountId ?? 0 },
    },
    enabled: accountId != null && accountId > 0,
    retry: false,
    staleTime: 60_000,
  });

  const usersById = useMemo(() => {
    if (query.data?.body.users == null) return null;
    const map = new Map<string, { email: string; firstName: string; lastName: string }>();
    for (const user of query.data.body.users) {
      map.set(String(user.user_id), {
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
      });
    }
    return map;
  }, [query.data]);

  return { usersById };
}

/**
 * Hook to check if a workspace has custom SMTP mail servers.
 */
export function useHasMailServers(workspaceId: string | undefined, accountState: string) {
  const skipStates: string[] = [AccountState.Canceled, AccountState.Suspended];

  return servicesQuery.getMailServers.useQuery({
    queryKey: ['masq-mail-servers', workspaceId],
    queryData: {
      params: { environment_id: Number(workspaceId ?? 0) },
    },
    enabled: workspaceId != null && !skipStates.includes(accountState),
    retry: false,
    select: (data) => data.body.mail_servers.length > 0,
  });
}
